import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Autocomplete,
    Checkbox,
    FormControlLabel,
    Chip,
    Box,
    Typography,
    Stack,
    CircularProgress,
} from "@mui/material";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { getAllUsers } from "../redux/slices/users";
import { getProjects } from "../redux/slices/project";
import { scheduleMeeting, getMeetingSlots } from "../redux/ducks/calendar";
import { showAlert } from "../redux/slices/alerts";
import { useHasPermission } from "../utils/permissionUtils";
import AppDateTimePicker from "./AppDateTimePicker";
import AppDatePicker from "./AppDatePicker";

const StyledContent = styled(DialogContent)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
}));

const Row = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
    alignItems: "center",
    flexWrap: "wrap",
}));

const DurationChip = styled(Chip, { shouldForwardProp: (prop) => prop !== "selected" })(({ theme, selected }) => ({
    cursor: "pointer",
    backgroundColor: selected ? theme.palette.primary.main : theme.palette.action.hover,
    color: selected ? theme.palette.primary.contrastText : theme.palette.text.primary,
    "&:hover": {
        backgroundColor: selected ? theme.palette.primary.dark : theme.palette.action.selected,
    },
}));

const ConflictBox = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.error.main}`,
    backgroundColor: theme.palette.error.light + "22",
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
}));

const ConflictTitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.error.main,
    fontWeight: theme.typography.fontWeightSemiBold,
    marginBottom: theme.spacing(0.5),
}));

const ConflictAttendee = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
}));

const ConflictBusy = styled(Typography)(({ theme }) => ({
    display: "block",
    marginLeft: theme.spacing(1),
}));

const SubmitSpinner = styled(CircularProgress)({ marginLeft: 8 });

const SlotSection = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
}));

const SlotGrid = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(84px, 1fr))",
    gap: theme.spacing(1),
}));

const SlotChip = styled(Chip, { shouldForwardProp: (prop) => prop !== "selected" })(({ theme, selected }) => ({
    cursor: "pointer",
    backgroundColor: selected ? theme.palette.primary.main : theme.palette.action.hover,
    color: selected ? theme.palette.primary.contrastText : theme.palette.text.primary,
    "&:hover": {
        backgroundColor: selected ? theme.palette.primary.dark : theme.palette.action.selected,
    },
}));

const SlotHelper = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const SlotState = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    padding: theme.spacing(1, 0),
}));

const DURATION_PRESETS = [
    { label: "15m", minutes: 15 },
    { label: "30m", minutes: 30 },
    { label: "60m", minutes: 60 },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeForSearch = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const fuzzyFilter = (getLabel) => (options, state) => {
    if (!Array.isArray(options)) return [];
    const q = normalizeForSearch(state?.inputValue);
    if (!q) return options;
    return options.filter((o) => normalizeForSearch(getLabel(o)).includes(q));
};

const nextHalfHour = () => {
    const d = new Date();
    d.setSeconds(0, 0);
    const minutes = d.getMinutes();
    if (minutes < 30) d.setMinutes(30);
    else {
        d.setMinutes(0);
        d.setHours(d.getHours() + 1);
    }
    return d;
};

const nextBusinessDay = () => {
    let d = dayjs().add(1, "day").startOf("day");
    while (d.day() === 0 || d.day() === 6) {
        d = d.add(1, "day");
    }
    return d.format("YYYY-MM-DD");
};

const formatSlotTime = (iso) => {
    try {
        return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    } catch (_) {
        return iso;
    }
};

const joinEmails = (invitees) =>
    (invitees || [])
        .map((i) => i.email)
        .filter(Boolean)
        .join(", ");

const browserTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch (_) {
        return null;
    }
};

const toLocalInputValue = (date) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatBusyWindow = (start, end) => {
    try {
        const s = new Date(start);
        const e = new Date(end);
        const sameDay = s.toDateString() === e.toDateString();
        const dateStr = s.toLocaleDateString();
        const sTime = s.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
        const eTime = e.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
        return sameDay ? `${dateStr} ${sTime}–${eTime}` : `${s.toLocaleString()} – ${e.toLocaleString()}`;
    } catch (_) {
        return `${start} – ${end}`;
    }
};

const ScheduleMeetingDialog = ({ open, onClose, defaultProject = null, defaultAttendees = [] }) => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const canManageCalendar = hasPermission("manageCalendar");
    const currentUser = useSelector((state) => state.auth.currentUser);
    const accountUsers = useSelector((state) => state.accountUsers?.users || []);
    const accountUsersAccountId = useSelector((state) => state.accountUsers?.accountId || null);

    const defaultAttendeesRef = useRef(defaultAttendees);
    const defaultProjectRef = useRef(defaultProject);
    const wasOpenRef = useRef(false);

    const [title, setTitleState] = useState("");
    const [attendees, setAttendeesState] = useState([]);
    const [attendeesInput, setAttendeesInput] = useState("");
    const [userOptions, setUserOptions] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);

    const [start, setStartState] = useState(toLocalInputValue(nextHalfHour()));
    const [durationLabel, setDurationLabelState] = useState("30m");

    const [date, setDateState] = useState(nextBusinessDay());
    const [override, setOverrideState] = useState(false);
    const [slots, setSlots] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [slotsDiag, setSlotsDiag] = useState(null);
    const [slotsUnconstrained, setSlotsUnconstrained] = useState([]);
    const [slotsError, setSlotsError] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const slotsRequestIdRef = useRef(0);

    const [description, setDescriptionState] = useState("");

    const [projectOptions, setProjectOptions] = useState([]);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [project, setProjectState] = useState(defaultProject);

    const [addMeetLink, setAddMeetLinkState] = useState(true);
    const [conflicts, setConflicts] = useState(null);
    const [confirmForce, setConfirmForce] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [inlineError, setInlineError] = useState(null);

    const clearForceIfNeeded = () => {
        if (confirmForce) {
            setConfirmForce(false);
            setConflicts(null);
        }
    };

    const setTitle = (v) => {
        clearForceIfNeeded();
        setTitleState(v);
    };
    const setAttendees = (v) => {
        clearForceIfNeeded();
        setAttendeesState(v);
    };
    const setStart = (v) => {
        clearForceIfNeeded();
        setStartState(v);
    };
    const setDurationLabel = (v) => {
        clearForceIfNeeded();
        setDurationLabelState(v);
    };
    const setDate = (v) => {
        clearForceIfNeeded();
        setDateState(v);
    };
    const setOverride = (v) => {
        clearForceIfNeeded();
        setOverrideState(v);
    };
    const setDescription = (v) => {
        clearForceIfNeeded();
        setDescriptionState(v);
    };
    const setProject = (v) => {
        clearForceIfNeeded();
        setProjectState(v);
    };
    const setAddMeetLink = (v) => {
        clearForceIfNeeded();
        setAddMeetLinkState(v);
    };

    useEffect(() => {
        if (open && !wasOpenRef.current) {
            defaultAttendeesRef.current = defaultAttendees;
            defaultProjectRef.current = defaultProject;

            setTitleState("");
            setStartState(toLocalInputValue(nextHalfHour()));
            setDurationLabelState("30m");
            setDateState(nextBusinessDay());
            setOverrideState(false);
            setSlots([]);
            setSlotsLoading(false);
            setSlotsDiag(null);
            setSlotsUnconstrained([]);
            setSlotsError(false);
            setSelectedSlot(null);
            setDescriptionState("");
            setAddMeetLinkState(true);
            setConflicts(null);
            setConfirmForce(false);
            setSubmitting(false);
            setInlineError(null);
            setProjectState(defaultProjectRef.current || null);

            const initialAttendees = [];
            if (currentUser && currentUser._id) {
                initialAttendees.push({
                    _id: currentUser._id,
                    fullName: currentUser.fullName || `${currentUser.first_name || ""} ${currentUser.last_name || ""}`.trim(),
                    email: currentUser.email,
                    isCurrentUser: true,
                });
            }
            (defaultAttendeesRef.current || []).forEach((u) => {
                if (u && u._id && !initialAttendees.some((a) => a._id === u._id)) {
                    initialAttendees.push(u);
                }
            });
            setAttendeesState(initialAttendees);
            setAttendeesInput("");
        }
        wasOpenRef.current = open;
    }, [open, currentUser]);

    useEffect(() => {
        if (!open) return;
        const onAccountPage = typeof window !== "undefined" && /^\/account\//.test(window.location.pathname);
        const useAccountScope = onAccountPage && accountUsers.length > 0 && !!accountUsersAccountId;

        if (useAccountScope) {
            setUserOptions(accountUsers.filter((u) => u?.active !== false && !u?.isDeleted && !u?.calendarOptOut));
            setUsersLoading(false);
            return;
        }
        setUsersLoading(true);
        dispatch(
            getAllUsers({
                callback: (data) => {
                    setUsersLoading(false);
                    const raw = Array.isArray(data) ? data : [];
                    setUserOptions(raw.filter((u) => u?.active !== false && u?.client?.isInternal === true && !u?.isDeleted && !u?.calendarOptOut));
                },
            }),
        );
        setProjectsLoading(true);
        dispatch(
            getProjects({
                start: 0,
                limit: 200,
                state: "open",
                callback: (data) => {
                    setProjectsLoading(false);
                    const list = Array.isArray(data) ? data : data?.projects || [];
                    setProjectOptions(list);
                },
            }),
        );
    }, [open, dispatch, accountUsers, accountUsersAccountId]);

    const durationMinutes = useMemo(() => {
        const preset = DURATION_PRESETS.find((p) => p.label === durationLabel);
        return preset ? preset.minutes : 30;
    }, [durationLabel]);

    const mappedAttendees = useMemo(
        () =>
            attendees
                .map((a) => {
                    if (a._id) return { polluxUserId: a._id, name: a.fullName || a.name || null };
                    if (a.email) return { email: a.email };
                    return null;
                })
                .filter(Boolean),
        [attendees],
    );

    const startIso = useMemo(() => {
        const startDate = start ? new Date(start) : null;
        if (!startDate || Number.isNaN(startDate.getTime())) return null;
        return startDate.toISOString();
    }, [start]);

    useEffect(() => {
        setSelectedSlot(null);
    }, [date, durationLabel, attendees, override]);

    useEffect(() => {
        if (!open || override) return;
        if (attendees.length === 0 || !date) {
            setSlots([]);
            setSlotsDiag(null);
            setSlotsUnconstrained([]);
            setSlotsError(false);
            return;
        }
        const requestId = slotsRequestIdRef.current + 1;
        slotsRequestIdRef.current = requestId;
        setSlotsLoading(true);
        setSlotsError(false);
        const timer = setTimeout(() => {
            dispatch(
                getMeetingSlots(
                    { date, durationMins: durationMinutes, attendees: mappedAttendees, timezone: browserTimezone() },
                    (data) => {
                        if (slotsRequestIdRef.current !== requestId) return;
                        setSlotsLoading(false);
                        if (!data) {
                            setSlots([]);
                            setSlotsDiag(null);
                            setSlotsUnconstrained([]);
                            setSlotsError(true);
                            return;
                        }
                        setSlots(Array.isArray(data.slots) ? data.slots : []);
                        setSlotsDiag(data.diagnostics || null);
                        setSlotsUnconstrained(Array.isArray(data.unconstrainedInvitees) ? data.unconstrainedInvitees : []);
                        setSlotsError(false);
                    },
                    () => {
                        if (slotsRequestIdRef.current !== requestId) return;
                        setSlotsLoading(false);
                    },
                ),
            );
        }, 300);
        return () => clearTimeout(timer);
    }, [open, override, date, durationMinutes, mappedAttendees, attendees.length, dispatch]);

    const startForSubmit = override ? startIso : selectedSlot;

    const canSubmit = Boolean(title.trim()) && attendees.length > 0 && Boolean(startForSubmit) && !submitting;

    const buildPayload = (force = false) => ({
        title: title.trim(),
        start: override ? startIso : selectedSlot,
        durationMins: durationMinutes,
        attendees: mappedAttendees,
        description: description || "",
        projectContext: project?._id || null,
        addMeetLink,
        timezone: browserTimezone(),
        force: override ? true : force,
    });

    const handleErrorCallback = (error) => {
        setSubmitting(false);
        const status = error?.response?.status;
        const data = error?.response?.data;
        if (status !== 409) return;
        if (data?.conflicts) {
            setConflicts(data.conflicts);
            setInlineError(null);
            if (canManageCalendar) setConfirmForce(true);
            return;
        }
        if (data?.error === "slot_unavailable") {
            const emails = (data.violations || []).map((v) => v.email).filter(Boolean);
            const who = emails.length > 0 ? ` (${emails.join(", ")})` : "";
            setInlineError(`Some attendees aren't available then (outside working hours, on time off, or too close to another meeting)${who}.`);
            setConflicts(null);
            if (canManageCalendar) setConfirmForce(true);
            return;
        }
        setInlineError("Could not schedule meeting due to conflicts");
    };

    const submit = (force = false) => {
        setSubmitting(true);
        setConflicts(null);
        setConfirmForce(false);
        setInlineError(null);
        dispatch(
            scheduleMeeting(
                buildPayload(force),
                (data) => {
                    setSubmitting(false);
                    dispatch(showAlert({ severity: "success", message: "Meeting scheduled" }));
                    if (onClose) onClose(data);
                },
                handleErrorCallback,
            ),
        );
    };

    const handleSubmit = () => submit(false);
    const handleForceSubmit = () => submit(true);
    const handleClose = () => {
        if (!submitting) onClose && onClose(null);
    };

    const handleAttendeesChange = (_e, value) => {
        const out = [];
        for (const item of value) {
            if (typeof item === "string") {
                const trimmed = item.trim();
                if (!EMAIL_RE.test(trimmed)) continue;
                if (out.some((a) => a.email && a.email.toLowerCase() === trimmed.toLowerCase())) continue;
                out.push({ email: trimmed });
            } else if (item && typeof item === "object") {
                if (out.some((a) => (a._id && a._id === item._id) || (a.email && item.email && a.email.toLowerCase() === item.email.toLowerCase()))) continue;
                out.push(item);
            }
        }
        setAttendees(out);
    };

    const attendeesFilter = useMemo(() => fuzzyFilter((o) => (typeof o === "string" ? o : `${o?.fullName || ""} ${o?.email || ""}`)), []);

    const attendeesHelperText = useMemo(() => {
        const trimmed = attendeesInput.trim();
        if (!trimmed) return " ";
        if (EMAIL_RE.test(trimmed)) return "Press Enter to add this email as an attendee";
        const q = normalizeForSearch(trimmed);
        const anyMatch = userOptions.some((u) => normalizeForSearch(`${u.fullName || ""} ${u.email || ""}`).includes(q));
        if (!anyMatch) return "Type a full email address (and press Enter) to invite someone who is not a user";
        return " ";
    }, [attendeesInput, userOptions]);

    const renderSlots = () => {
        if (slotsLoading) {
            return (
                <SlotState>
                    <CircularProgress size={18} />
                    <Typography variant="body2">Finding open times…</Typography>
                </SlotState>
            );
        }
        if (slotsError) {
            return (
                <SlotState>
                    <Typography variant="body2">Couldn't load available times. Try again.</Typography>
                </SlotState>
            );
        }
        if (attendees.length === 0) {
            return (
                <SlotState>
                    <Typography variant="body2">Add attendees to see open times.</Typography>
                </SlotState>
            );
        }
        if (slots.length === 0) {
            if (slotsDiag?.reason === "no_internal_host") {
                return (
                    <SlotState>
                        <Typography variant="body2">Add at least one team member — they'll host the meeting.</Typography>
                    </SlotState>
                );
            }
            const noSchedule = slotsDiag?.noScheduleInvitees || [];
            const timeOff = slotsDiag?.timeOffInvitees || [];
            const parts = [];
            if (noSchedule.length > 0) parts.push(`${joinEmails(noSchedule)} haven't set working hours`);
            if (timeOff.length > 0) parts.push(`${joinEmails(timeOff)} are on approved time off that day`);
            const message = parts.length > 0 ? `${parts.join("; ")}.` : "No common openings on this day. Try another day or a shorter meeting.";
            return (
                <SlotState>
                    <Typography variant="body2">{message}</Typography>
                </SlotState>
            );
        }
        return (
            <>
                <SlotGrid>
                    {slots.map((iso) => (
                        <SlotChip key={iso} label={formatSlotTime(iso)} selected={selectedSlot === iso} onClick={() => setSelectedSlot(iso)} disabled={submitting} />
                    ))}
                </SlotGrid>
                {renderUnconstrained()}
            </>
        );
    };

    const renderUnconstrained = () => {
        if (!slotsUnconstrained || slotsUnconstrained.length === 0) return null;
        return <SlotHelper variant="caption">Not checked for availability: {joinEmails(slotsUnconstrained)} (external guests).</SlotHelper>;
    };

    const renderConflicts = () => (
        <ConflictBox>
            <ConflictTitle variant="body2">{confirmForce ? "Conflicts detected — schedule anyway?" : "Attendees are busy at this time"}</ConflictTitle>
            <Stack spacing={0.5}>
                {(conflicts || []).map((c, idx) => (
                    <Box key={`${c.email}-${idx}`}>
                        <ConflictAttendee variant="body2">{c.email}</ConflictAttendee>
                        {(c.busy || []).map((w, i) => (
                            <ConflictBusy key={i} variant="caption" color="text.secondary">
                                {formatBusyWindow(w.start, w.end)}
                            </ConflictBusy>
                        ))}
                    </Box>
                ))}
            </Stack>
        </ConflictBox>
    );

    const renderInlineError = () => (
        <ConflictBox>
            <ConflictTitle variant="body2">{inlineError}</ConflictTitle>
        </ConflictBox>
    );

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>Schedule Meeting</DialogTitle>
            <StyledContent>
                <TextField label="Title" required fullWidth value={title} onChange={(e) => setTitle(e.target.value)} disabled={submitting} />

                <Autocomplete
                    multiple
                    freeSolo
                    options={userOptions}
                    loading={usersLoading}
                    value={attendees}
                    onInputChange={(_, v) => setAttendeesInput(v)}
                    onChange={handleAttendeesChange}
                    getOptionLabel={(o) => (typeof o === "string" ? o : o?.fullName || o?.email || "")}
                    isOptionEqualToValue={(o, v) => {
                        if (o?._id && v?._id) return String(o._id) === String(v._id);
                        if (o?.email && v?.email) return o.email.toLowerCase() === v.email.toLowerCase();
                        return false;
                    }}
                    filterOptions={attendeesFilter}
                    filterSelectedOptions
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                            const tagProps = getTagProps({ index });
                            const { onDelete, ...rest } = tagProps;
                            return (
                                <Chip
                                    {...rest}
                                    key={option._id || option.email}
                                    label={option.fullName || option.email}
                                    size="small"
                                    onDelete={option.isCurrentUser ? undefined : onDelete}
                                />
                            );
                        })
                    }
                    renderInput={(params) => (
                        <TextField {...params} label="Attendees" required placeholder="Add people or type an email and press Enter" helperText={attendeesHelperText} />
                    )}
                    disabled={submitting}
                />

                <Row>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        {DURATION_PRESETS.map((p) => (
                            <DurationChip key={p.label} label={p.label} selected={durationLabel === p.label} onClick={() => setDurationLabel(p.label)} disabled={submitting} />
                        ))}
                    </Stack>
                </Row>

                {canManageCalendar && (
                    <FormControlLabel
                        control={<Checkbox checked={override} onChange={(e) => setOverride(e.target.checked)} disabled={submitting} />}
                        label="Pick any time (override availability)"
                    />
                )}

                {override ? (
                    <AppDateTimePicker label="Start" value={start} onChange={(v) => setStart(v)} minDateTime={dayjs().add(1, "hour")} disablePast disabled={submitting} required />
                ) : (
                    <SlotSection>
                        <AppDatePicker label="Date" value={date} onChange={(v) => setDate(v)} disablePast minDate={dayjs().format("YYYY-MM-DD")} disabled={submitting} required />
                        <SlotHelper variant="caption">Showing times when everyone is free, in your timezone.</SlotHelper>
                        {renderSlots()}
                    </SlotSection>
                )}

                <TextField label="Description" multiline rows={3} fullWidth value={description} onChange={(e) => setDescription(e.target.value)} disabled={submitting} />

                <Autocomplete
                    options={projectOptions}
                    loading={projectsLoading}
                    value={project}
                    onChange={(_, v) => setProject(v)}
                    getOptionLabel={(o) => o?.name || ""}
                    isOptionEqualToValue={(o, v) => o?._id === v?._id}
                    filterOptions={fuzzyFilter((o) => o?.name || "")}
                    renderInput={(params) => <TextField {...params} label="Project context (optional)" placeholder="Link to a project" />}
                    disabled={submitting || !!defaultProjectRef.current}
                />

                <FormControlLabel
                    control={<Checkbox checked={addMeetLink} onChange={(e) => setAddMeetLink(e.target.checked)} disabled={submitting} />}
                    label="Add Google Meet link"
                />

                {conflicts && renderConflicts()}
                {!conflicts && inlineError && renderInlineError()}
            </StyledContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={submitting}>
                    Cancel
                </Button>
                {confirmForce ? (
                    <>
                        <Button
                            onClick={() => {
                                setConflicts(null);
                                setConfirmForce(false);
                                setInlineError(null);
                            }}
                            disabled={submitting}
                        >
                            Pick another time
                        </Button>
                        <Button variant="contained" color="warning" onClick={handleForceSubmit} disabled={submitting}>
                            Schedule anyway
                            {submitting && <SubmitSpinner size={16} />}
                        </Button>
                    </>
                ) : (
                    <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
                        Schedule
                        {submitting && <SubmitSpinner size={16} />}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ScheduleMeetingDialog;
